
import * as Comlink from "comlink";
import { useEffect, useRef } from "react";
import { runtimeEnv } from "configuration";
import { useAppDispatch, useAppSelector } from "store";
import { globalSettingsChanged, layoutStateChanged, themeChanged, TranspilerStatus } from "store/features";
import { GlobalSettings, LayoutState, SandboxEvents } from "types";
import { ThemeType } from "@itwin/itwinui-react";
import { TestReport } from "@sandbox/code-editor";
import { getTargetOrigin } from "utils/helpers";

const parentEndpoint = Comlink.windowEndpoint(self.parent);
const externalHost = Comlink.wrap<SandboxEvents>(parentEndpoint);

const sandboxEventType = "sandbox-event";

let exposed = false;

export const useSandboxAutomation = () => {
  const dispatch = useAppDispatch();
  const sandbox = useAppSelector((state) => state.activeSandbox.metadata);
  const sessionId = useAppSelector((state) => state.settings.global.sessionId);
  const transpilerStatus = useAppSelector((state) => state.transpiler.status);
  const testReport = useAppSelector((state) => state.test.report);
  const prevTranspilerStatus = usePreviousTranspilerStatus(transpilerStatus);

  const api = {
    setTheme: (theme: ThemeType) => {
      dispatch(themeChanged(theme));
    },
    setLayout: (layout: LayoutState) => {
      dispatch(layoutStateChanged(layout));
    },
    setSettings: (settings: GlobalSettings) => {
      dispatch(globalSettingsChanged(settings));
    },

    // very basic implementation to send envets to preview
    // it will only work if preview iframe is fully loaded
    // otherwise external event gets lost
    raise: (eventId: string, data?: any) => {
      const previewFrame = document.getElementById("sample-frame-container");
      if (previewFrame) {
        //@ts-ignore
        previewFrame.contentWindow.postMessage({ eventId, data, type: sandboxEventType }, getTargetOrigin())
      }
    }
  };

  if (!exposed) {
    Comlink.expose(api, parentEndpoint, runtimeEnv.REACT_APP_ALLOWED_ORIGINS?.split(";"));
    exposed = true;
  }

  const events = {
    onTranspiled: ({ sessionId, sandboxName, sandboxId }: { sessionId?: string, sandboxName?: string, sandboxId?: string }) => externalHost.onTranspiled({ sessionId, sandboxName, sandboxId }),
    onTestReport: ({ sessionId, testReport }: { sessionId?: string, testReport?: TestReport[] }) => externalHost.onTestReport({ sessionId, testReport })
  };

  // redispatch events originating in user code
  useEffect(() => {
    const sandboxListener = (event: MessageEvent<any>) => {
      const { eventId, type, data } = event.data;
      if (type === sandboxEventType) {
        externalHost.onSandboxEvent({ sessionId, eventId, data });
      }
    }

    globalThis.addEventListener("message", sandboxListener);

    return () => {
      globalThis.removeEventListener("message", sandboxListener);
    }
  }, [])

  useEffect(() => {
    if (transpilerStatus === "ready" && prevTranspilerStatus === "building") {
      events.onTranspiled({
        sessionId,
        sandboxName: sandbox?.name,
        sandboxId: sandbox?.id
      });
    }
  }, [transpilerStatus])

  useEffect(() => {
    if (testReport) {
      events.onTestReport({ sessionId, testReport });
    }
  }, [testReport])
}


function usePreviousTranspilerStatus(status: TranspilerStatus) {
  const ref = useRef<TranspilerStatus>();
  useEffect(() => {
    ref.current = status;
  }, [status]);
  return ref.current;
}
